// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { css, cx } from '@emotion/css';
import { Theme, useTheme, Tooltip } from '@mui/material';
import { BiPlus, BiMinus } from 'react-icons/bi';
import {
  MdOpenWith,
  MdClose,
  MdKeyboardArrowUp,
  MdKeyboardArrowDown,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
} from 'react-icons/md';
import { useMap } from '@vis.gl/react-google-maps';

import GoogleMapsElement from './GoogleMapsElement';
import { useMapPreferences } from './useMapPreferences';
import { useMapSurfaceColors } from './useMapSurfaceColors';

// TYPES ---------------------------------------------------------------- //

interface MapNavigationProps {
  style?: React.CSSProperties;
  className?: string;
  mapId?: string;
}

const PAN_DISTANCE_PX = 100;

// COMPONENT ---------------------------------------------------------------- //

const MapNavigation = ({ style, className, mapId }: MapNavigationProps) => {
  const theme = useTheme();
  const { mode } = useMapPreferences();
  const surface = useMapSurfaceColors();
  const cls = useStyles(theme, mode, surface.divider);
  const map = useMap(mapId);

  const [isOpen, setIsOpen] = React.useState(false);

  const panBy = (x: number, y: number) => map?.panBy(x, y);
  const zoomIn = () => map?.setZoom((map.getZoom() ?? 10) + 1);
  const zoomOut = () => map?.setZoom((map.getZoom() ?? 10) - 1);

  const togglePositionStyle: React.CSSProperties = { bottom: 24, right: 10, ...style };
  const controlsPositionStyle: React.CSSProperties = { bottom: 24, right: 58, ...style };

  const toggleButton = (
    <Tooltip title={isOpen ? 'Close navigation controls' : 'Open navigation controls'} placement='left'>
      <button
        type='button'
        aria-label={isOpen ? 'Close navigation controls' : 'Open navigation controls'}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(v => !v)}
        className={isOpen ? cls.btnActive : cls.btnInactive}
      >
        {isOpen ? <MdClose size={18} /> : <MdOpenWith size={18} />}
      </button>
    </Tooltip>
  );

  return (
    <>
      {isOpen && (
        <GoogleMapsElement style={controlsPositionStyle} disablePadding>
          <div className={cls.controlsRow}>
            {/* Direction pad */}
            <div className={cls.pad}>
              <Tooltip title='Pan left' placement='left'>
                <button
                  type='button'
                  aria-label='Pan left'
                  onClick={() => panBy(-PAN_DISTANCE_PX, 0)}
                  className={cx(cls.btnInactive, cls.padLeft)}
                >
                  <MdKeyboardArrowLeft size={22} />
                </button>
              </Tooltip>
              <Tooltip title='Pan up' placement='top'>
                <button
                  type='button'
                  aria-label='Pan up'
                  onClick={() => panBy(0, -PAN_DISTANCE_PX)}
                  className={cx(cls.btnInactive, cls.padUp)}
                >
                  <MdKeyboardArrowUp size={22} />
                </button>
              </Tooltip>
              <Tooltip title='Pan right' placement='right'>
                <button
                  type='button'
                  aria-label='Pan right'
                  onClick={() => panBy(PAN_DISTANCE_PX, 0)}
                  className={cx(cls.btnInactive, cls.padRight)}
                >
                  <MdKeyboardArrowRight size={22} />
                </button>
              </Tooltip>
              <Tooltip title='Pan down' placement='bottom'>
                <button
                  type='button'
                  aria-label='Pan down'
                  onClick={() => panBy(0, PAN_DISTANCE_PX)}
                  className={cx(cls.btnInactive, cls.padDown)}
                >
                  <MdKeyboardArrowDown size={22} />
                </button>
              </Tooltip>
            </div>

            {/* Zoom stack */}
            <div className={cls.zoomStack}>
              <Tooltip title='Zoom in' placement='left'>
                <button type='button' aria-label='Zoom in' onClick={zoomIn} className={cls.btnInactive}>
                  <BiPlus size={20} />
                </button>
              </Tooltip>
              <Tooltip title='Zoom out' placement='left'>
                <button type='button' aria-label='Zoom out' onClick={zoomOut} className={cls.btnInactive}>
                  <BiMinus size={20} />
                </button>
              </Tooltip>
            </div>
          </div>
        </GoogleMapsElement>
      )}

      <GoogleMapsElement style={togglePositionStyle} className={className} disablePadding>
        <div className={cls.collapsedStack}>{toggleButton}</div>
      </GoogleMapsElement>
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme, mode: 'light' | 'dark', dividerColor?: string) => {
  const primaryMain = theme?.palette?.primary?.main || '#1976d2';
  const primaryFaded = `${primaryMain}20`;
  const isLight = mode === 'light';
  const inactiveFg = isLight
    ? theme?.palette?.text?.secondary || 'rgba(0, 0, 0, 0.55)'
    : theme?.palette?.text?.contrastSecondary || 'rgba(255, 255, 255, 0.7)';
  const inactiveHoverBg = isLight
    ? theme?.palette?.action?.hover || 'rgba(0, 0, 0, 0.04)'
    : 'rgba(255, 255, 255, 0.08)';

  const btnBase = css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 150ms ease, color 150ms ease;
  `;

  return {
    collapsedStack: css`
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 4px;
    `,
    controlsRow: css`
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 6px;
      padding: 4px;
    `,
    pad: css`
      display: grid;
      grid-template-columns: repeat(3, 32px);
      grid-template-rows: repeat(2, 32px);
      gap: 4px 2px;
    `,
    padUp: css`
      grid-column: 2;
      grid-row: 1;
    `,
    padDown: css`
      grid-column: 2;
      grid-row: 2;
    `,
    padLeft: css`
      grid-column: 1;
      grid-row: 1 / span 2;
      align-self: center;
    `,
    padRight: css`
      grid-column: 3;
      grid-row: 1 / span 2;
      align-self: center;
    `,
    zoomStack: css`
      display: flex;
      flex-direction: column;
      gap: 4px;
      border-left: 1px solid ${dividerColor || 'rgba(255, 255, 255, 0.1)'};
      padding-left: 6px;
    `,
    btnInactive: cx(
      btnBase,
      css`
        color: ${inactiveFg};
        &:hover {
          background-color: ${inactiveHoverBg};
        }
      `
    ),
    btnActive: cx(
      btnBase,
      css`
        background-color: ${primaryFaded};
        color: ${primaryMain};
        &:hover {
          background-color: ${primaryFaded};
        }
      `
    ),
  };
};

// EXPORT ---------------------------------------------------------------- //

export default MapNavigation;
